// Zone-aware conversion between instants and wall-clock readings.
//
// ZERO RUNTIME DEPENDENCIES, on purpose. See CONTRACT.md "Why no time library".
// The IANA rules come from the JavaScript runtime's own ICU data via `Intl`,
// which is present in Node and in every browser we target, and which is updated
// by the platform rather than by us remembering to bump a polyfill.

import type { Instant, TimeZoneId, WallClock, ZonedResolution } from "./types.js";

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;

/**
 * `Intl.DateTimeFormat` construction is not free and we do it on every
 * conversion, so formatters are memoised per zone. Bounded by the number of
 * distinct zones a process sees, which for this product is approximately one.
 */
const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatterFor(zone: TimeZoneId): Intl.DateTimeFormat {
  const cached = formatterCache.get(zone);
  if (cached !== undefined) return cached;

  // `en-US` with hour12:false is a deliberate, locale-independent choice: we are
  // parsing these parts numerically, never showing them. Display formatting is a
  // UI concern and does not belong in the kernel.
  const created = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  formatterCache.set(zone, created);
  return created;
}

/**
 * True if the runtime recognises `candidate` as an IANA zone AND it is a real
 * named zone rather than a fixed offset.
 *
 * Used at the edges (circle creation, CIR-003/CIR-012) so an unknown zone is
 * rejected where a human can still fix it, rather than throwing somewhere deep
 * in a coverage calculation months later.
 *
 * THE OFFSET CHECK IS NOT PEDANTRY. `Intl` happily accepts `"+01:00"`,
 * `"-05:00"` and `"+0100"` as time zones and resolves them to themselves. Such a
 * value looks like it works — every conversion succeeds — but it pins the zone
 * to one offset forever, so British Summer Time silently never arrives and every
 * commitment between late March and late October is an hour wrong. That is
 * exactly the failure plan section 28.12 forbids ("Never use a fixed
 * +00:00/+01:00 offset as the time-zone model") and exactly the Critical risk
 * R-024 describes. It fails in a way nobody notices until somebody misses a
 * hospital appointment, so it is refused here at the edge.
 *
 * Known sharp edge we do NOT reject: `Etc/GMT+3` and friends are genuine IANA
 * identifiers, so they pass — but they are fixed-offset zones with a notoriously
 * inverted sign. A circle should be configured with a real regional zone such as
 * `Europe/London` (CIR-012).
 */
export function isValidTimeZone(candidate: string): candidate is TimeZoneId {
  let resolved: string;
  try {
    resolved = new Intl.DateTimeFormat("en-US", { timeZone: candidate }).resolvedOptions().timeZone;
  } catch {
    return false;
  }
  // An offset-form zone resolves to something beginning with a sign.
  return !resolved.startsWith("+") && !resolved.startsWith("-");
}

/**
 * Narrow a string to a `TimeZoneId`, throwing if the runtime does not know it.
 *
 * Throwing is correct here and nowhere else in this module: an unrecognised zone
 * is a programming or configuration error, not a domain outcome a user can be
 * shown a calm message about.
 */
export function timeZone(candidate: string): TimeZoneId {
  if (!isValidTimeZone(candidate)) {
    throw new RangeError(`Unknown IANA time zone: ${candidate}`);
  }
  return candidate;
}

/** Narrow epoch milliseconds to an `Instant`. */
export function instant(epochMilliseconds: number): Instant {
  if (!Number.isFinite(epochMilliseconds)) {
    throw new RangeError(
      `Instant must be a finite epoch-millisecond value, got ${String(epochMilliseconds)}`,
    );
  }
  return epochMilliseconds as Instant;
}

/**
 * What the clock on the wall read, in `zone`, at `at`.
 *
 * Total: every instant has exactly one wall-clock reading in a given zone. It is
 * the inverse direction that is lossy, which is why that one returns a union.
 */
export function toWallClock(at: Instant, zone: TimeZoneId): WallClock {
  const parts = formatterFor(zone).formatToParts(new Date(at));

  const valueOf = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? "0";

  // Some ICU versions render midnight as "24" under hour12:false.
  const rawHour = valueOf("hour");

  return {
    year: Number(valueOf("year")),
    month: Number(valueOf("month")),
    day: Number(valueOf("day")),
    hour: rawHour === "24" ? 0 : Number(rawHour),
    minute: Number(valueOf("minute")),
  };
}

/**
 * The offset (in minutes east of UTC) that `zone` was applying at `at`.
 *
 * Derived, never stored — see the `TimeZoneId` doc comment. Exposed because
 * calendar export (ICS) and display genuinely need the offset that applied at a
 * specific moment; it must never be cached and reused for a different moment.
 */
export function offsetMinutesAt(at: Instant, zone: TimeZoneId): number {
  const wall = toWallClock(at, zone);
  const asIfUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute);
  // Truncate `at` to whole minutes so sub-minute components (which the wall
  // reading does not carry) cannot leak into the offset.
  const truncated = Math.floor(at / MINUTE_MS) * MINUTE_MS;
  return (asIfUtc - truncated) / MINUTE_MS;
}

/**
 * Which instant (or instants, or none) does this wall-clock reading name in this
 * zone?
 *
 * The algorithm: guess an offset by probing the timeline on both sides of the
 * naive UTC interpretation, then keep only those candidates that actually read
 * back as the requested wall time. A gap yields zero survivors; an overlap
 * yields two. Probing +/- 26 hours covers every real-world DST shift and the
 * largest historical zone-offset changes, both far larger than the usual hour.
 *
 * This is the one function in the kernel where getting it wrong silently moves
 * somebody's responsibility time (R-024, Critical), so it is deliberately
 * written to over-check rather than to be clever.
 */
export function resolveWallClock(wall: WallClock, zone: TimeZoneId): ZonedResolution {
  const naive = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute);

  const candidates = new Set<number>();
  for (const probeOffsetHours of [-26, 0, 26]) {
    const probe = naive + probeOffsetHours * HOUR_MS;
    const offset = offsetMinutesAt(probe as Instant, zone);
    const candidate = naive - offset * MINUTE_MS;
    if (readsBackAs(candidate, wall, zone)) {
      candidates.add(candidate);
    }
  }

  const sorted = [...candidates].sort((a, b) => a - b);

  const [first, second] = sorted;

  if (first === undefined) {
    return { kind: "gap", wall, zone };
  }
  if (second !== undefined) {
    return {
      kind: "ambiguous",
      wall,
      zone,
      earlier: first as Instant,
      later: second as Instant,
    };
  }
  return { kind: "ok", instant: first as Instant };
}

function readsBackAs(candidate: number, wall: WallClock, zone: TimeZoneId): boolean {
  const actual = toWallClock(candidate as Instant, zone);
  return (
    actual.year === wall.year &&
    actual.month === wall.month &&
    actual.day === wall.day &&
    actual.hour === wall.hour &&
    actual.minute === wall.minute
  );
}
